import {
  toISO, dayOfWeek, daysInMonth, formatLong,
  DAY_NAMES_ID, MONTH_NAMES_ID, MONTH_SLUGS_ID,
} from "./dates";
import { gregorianToHijri, formatHijri, type HijriDate } from "./hijri";
import { gregorianToJawa, getPasaran, type JawaDate } from "./jawa";
import { gregorianToBali, type BaliDate } from "./bali";
import { gregorianToChinese, type ChineseDate } from "./china";
import {
  getHolidaysForDate, getHolidaysForMonth, type Holiday,
} from "@/data/holidays";
import { getHariPentingForDate, type HariPenting } from "@/data/hari-penting";

export interface DayInfo {
  year: number;
  month: number;
  day: number;
  iso: string;
  dayName: string; // Senin, etc.
  dayOfWeek: number; // 0..6
  isWeekend: boolean;
  isSunday: boolean;
  formattedLong: string;
  hijri: HijriDate;
  hijriFormatted: string;
  jawa: JawaDate;
  bali: BaliDate;
  china: ChineseDate;
  holidays: Holiday[];
  hariPenting: HariPenting[];
  isLibur: boolean; // libur nasional
  isCutiBersama: boolean;
  isTanggalMerah: boolean; // Sunday OR libur nasional
  pathDate: string; // /7-juni-2026
  pathMonth: string; // /juni-2026
  pasaran: string;
}

export function getDayInfo(year: number, month: number, day: number): DayInfo {
  const iso = toISO(year, month, day);
  const dow = dayOfWeek(year, month, day);
  const dayName = DAY_NAMES_ID[dow];
  const hijri = gregorianToHijri(year, month, day);
  const jawa = gregorianToJawa(year, month, day);
  const bali = gregorianToBali(year, month, day);
  const china = gregorianToChinese(year, month, day);
  const holidays = getHolidaysForDate(iso);
  const hariPenting = getHariPentingForDate(iso);
  const isLibur = holidays.some((h) => h.type === "libur");
  const isCutiBersama = holidays.some((h) => h.type === "cuti-bersama");
  const isSunday = dow === 0;
  return {
    year, month, day, iso,
    dayName, dayOfWeek: dow,
    isWeekend: dow === 0 || dow === 6,
    isSunday,
    formattedLong: formatLong(year, month, day),
    hijri,
    hijriFormatted: formatHijri(hijri),
    jawa,
    bali,
    china,
    holidays,
    hariPenting,
    isLibur,
    isCutiBersama,
    isTanggalMerah: isSunday || isLibur,
    pathDate: `/kalender/${year}/${day}-${MONTH_SLUGS_ID[month - 1]}`,
    pathMonth: `/kalender/${year}/${MONTH_SLUGS_ID[month - 1]}`,
    pasaran: getPasaran(year, month, day),
  };
}

/** Build month grid: 6 rows x 7 cols of day cells (with leading/trailing nulls). */
export interface MonthCell {
  /** null = blank cell */
  info: DayInfo | null;
  inMonth: boolean;
}

export function buildMonthGrid(year: number, month: number, align: "auto" | "fixed" = "fixed"): MonthCell[] {
  const firstDow = dayOfWeek(year, month, 1);
  const total = daysInMonth(year, month);
  const cells: MonthCell[] = [];
  // Leading blanks (Sunday=0 first column)
  for (let i = 0; i < firstDow; i++) cells.push({ info: null, inMonth: false });
  for (let d = 1; d <= total; d++) cells.push({ info: getDayInfo(year, month, d), inMonth: true });
  if (align === "fixed") {
    // Pad to 6 full weeks (42 cells) so every month grid renders the
    // same height — used on the standalone month detail page.
    while (cells.length < 42) cells.push({ info: null, inMonth: false });
  } else {
    // Pad only to the end of the last used week — no trailing empty rows.
    while (cells.length % 7 !== 0) cells.push({ info: null, inMonth: false });
  }
  return cells;
}

export interface MonthSummary {
  year: number;
  month: number;
  monthName: string;
  totalDays: number;
  holidays: Holiday[];
  liburNasional: Holiday[];
  cutiBersama: Holiday[];
  redDates: string[]; // ISO dates that are red (libur nasional or Sunday)
}

export function getMonthSummary(year: number, month: number): MonthSummary {
  const cached = MONTH_SUMMARY_CACHE.get(year * 100 + month);
  if (cached) return cached;
  const holidays = getHolidaysForMonth(year, month);
  const total = daysInMonth(year, month);
  const reds: string[] = [];
  for (let d = 1; d <= total; d++) {
    const dow = dayOfWeek(year, month, d);
    const iso = toISO(year, month, d);
    if (dow === 0 || holidays.some((h) => h.date === iso && h.type === "libur")) {
      reds.push(iso);
    }
  }
  const summary: MonthSummary = {
    year, month,
    monthName: MONTH_NAMES_ID[month - 1],
    totalDays: total,
    holidays,
    liburNasional: holidays.filter((h) => h.type === "libur"),
    cutiBersama: holidays.filter((h) => h.type === "cuti-bersama"),
    redDates: reds,
  };
  MONTH_SUMMARY_CACHE.set(year * 100 + month, summary);
  return summary;
}

/** Memoization caches. Calendar data is immutable per (year, month) so we
 *  can keep results indefinitely; bound size to avoid leaks if exotic
 *  inputs are ever requested. */
const MONTH_SUMMARY_CACHE = new Map<number, MonthSummary>();
const WORKDAYS_CACHE = new Map<string, ReturnType<typeof computeWorkdaysBetween>>();
const LIST_WORKDAYS_CACHE = new Map<string, ReturnType<typeof computeListWorkdaysBetween>>();
const MAX_CACHE = 512;
function trimCache<K, V>(m: Map<K, V>) {
  if (m.size <= MAX_CACHE) return;
  const drop = m.size - MAX_CACHE;
  const it = m.keys();
  for (let i = 0; i < drop; i++) {
    const k = it.next().value as K | undefined;
    if (k === undefined) break;
    m.delete(k);
  }
}

/** Workdays (Mon-Fri excluding national holidays) between two ISO dates, inclusive. */
export function workdaysBetween(aISO: string, bISO: string): {
  totalDays: number;
  workdays: number;
  weekends: number;
  liburNasional: number;
} {
  if (aISO > bISO) [aISO, bISO] = [bISO, aISO];
  const key = `${aISO}|${bISO}`;
  const hit = WORKDAYS_CACHE.get(key);
  if (hit) return hit;
  const out = computeWorkdaysBetween(aISO, bISO);
  WORKDAYS_CACHE.set(key, out);
  trimCache(WORKDAYS_CACHE);
  return out;
}

function computeWorkdaysBetween(aISO: string, bISO: string): {
  totalDays: number;
  workdays: number;
  weekends: number;
  liburNasional: number;
} {
  let cur = aISO;
  let workdays = 0, weekends = 0, libur = 0, total = 0;
  while (cur <= bISO) {
    total += 1;
    const [y, m, d] = cur.split("-").map(Number);
    const dow = dayOfWeek(y, m, d);
    const isLib = getHolidaysForDate(cur).some((h) => h.type === "libur");
    if (dow === 0 || dow === 6) weekends += 1;
    if (isLib) libur += 1;
    if (dow !== 0 && dow !== 6 && !isLib) workdays += 1;
    // increment date
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 1);
    cur = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
  }
  return { totalDays: total, workdays, weekends, liburNasional: libur };
}

export interface DayEntry {
  iso: string;
  dayName: string;
  type: "kerja" | "akhir-pekan" | "libur";
  liburName?: string;
}

/** Detailed list of every day between two ISO dates (inclusive) classified by type.
 *  Hard-cap at 5000 entries to protect SSR/CPU. Memoized per (from, to, max). */
export function listWorkdaysBetween(aISO: string, bISO: string, max = 5000): {
  entries: DayEntry[];
  truncated: boolean;
  total: number;
  workdays: number;
  weekends: number;
  liburNasional: number;
} {
  if (aISO > bISO) [aISO, bISO] = [bISO, aISO];
  const key = `${aISO}|${bISO}|${max}`;
  const hit = LIST_WORKDAYS_CACHE.get(key);
  if (hit) return hit;
  const out = computeListWorkdaysBetween(aISO, bISO, max);
  LIST_WORKDAYS_CACHE.set(key, out);
  trimCache(LIST_WORKDAYS_CACHE);
  return out;
}

function computeListWorkdaysBetween(aISO: string, bISO: string, max: number): {
  entries: DayEntry[];
  truncated: boolean;
  total: number;
  workdays: number;
  weekends: number;
  liburNasional: number;
} {
  let cur = aISO;
  const entries: DayEntry[] = [];
  let workdays = 0, weekends = 0, libur = 0, total = 0;
  let truncated = false;
  while (cur <= bISO) {
    total += 1;
    const [y, m, d] = cur.split("-").map(Number);
    const dow = dayOfWeek(y, m, d);
    const libs = getHolidaysForDate(cur).filter((h) => h.type === "libur");
    const isLib = libs.length > 0;
    const isWknd = dow === 0 || dow === 6;
    if (isWknd) weekends += 1;
    if (isLib) libur += 1;
    let type: DayEntry["type"] = "kerja";
    if (isLib) type = "libur";
    else if (isWknd) type = "akhir-pekan";
    else workdays += 1;
    if (entries.length < max) {
      entries.push({
        iso: cur,
        dayName: DAY_NAMES_ID[dow],
        type,
        liburName: libs[0]?.name,
      });
    } else {
      truncated = true;
    }
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 1);
    cur = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
  }
  return { entries, truncated, total, workdays, weekends, liburNasional: libur };
}